<template>
  <div class="h-100 w-100" style="position: relative">
    <div :id="id" class="h-100 w-100"></div>
    <div class="map-body">
      <slot name="body"></slot>
      <MapTypeToggle v-if="showMapTypeToggle" :map="map" :placement="mapTypeTogglePlacement">
        <slot name="mapTypeExtra"></slot>
      </MapTypeToggle>
    </div>
    <div v-html="css"></div>
  </div>
</template>

<script>
import MapTypeToggle from './MapTypeToggle.vue';

export default {
  components: {
    MapTypeToggle
  },
  props: ["showlayers", "zoomshow", "mapTypeId", "showMapTypeToggle", "mapTypeTogglePlacement"],
  data() {
    return {
      css: null,
      id: "map-" + this.$.uid,
      map: null,
      markerGroup: [],
      markers: [],
      event: {},
      points: [],
      max_points: 50,
      infoWindow: null,
      isLoaded: false,
      polygon: null,
      // Polígonos de camadas de dados (hoje: avisos do INMET). Separado de `polygon`, que é
      // o polígono ÚNICO e editável da área agrícola — misturar os dois faria um `setPath`
      // da área apagar os avisos, e o clear dos avisos apagar a área do usuário.
      layerPolygons: [],
      // Ícones dos avisos (overlays HTML). Lista separada dos polígonos porque o ciclo de
      // vida é diferente: overlays têm onRemove() e podem ser recriados a cada redesenho;
      // polígonos são reaproveitados num pool (ver addPolygons).
      layerIcons: [],
      // Overlay canvas que desenha as áreas dos avisos (ver addPolygons).
      avisosOverlay: null,
      projectionOverlay: null,
      prevSignatures: null, // Set de assinaturas do render anterior (anima só o que mudou)
    };
  },
  methods: {
    async loadGoogleMapsScript() {
      if (window.google && window.google.maps) {
        return true;
      }

      return new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          existingScript.addEventListener('load', () => resolve(true));
          existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps')));
          return;
        }

        const script = document.createElement('script');
        const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyC6ZPMCgYaQ2X3s4qfryNSoK-fNYBDcLWk';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,drawing`;
        script.async = true;
        script.defer = true;

        script.addEventListener('load', () => resolve(true));
        script.addEventListener('error', () => reject(new Error('Failed to load Google Maps')));

        document.head.appendChild(script);
      });
    },

    async initMap() {
      try {
        await this.loadGoogleMapsScript();

        let latLngBegin = { lat: -29.452491, lng: -51.959624 };

        this.map = new google.maps.Map(document.getElementById(this.id), {
          center: latLngBegin,
          zoom: 12,
          minZoom: 4,   // impede afastar a ponto de ver o mundo repetido
          maxZoom: 20,
          mapTypeId: this.mapTypeId || 'roadmap',
          mapTypeControl: false,
          zoomControl: this.zoomshow == null ? true : this.zoomshow,
          streetViewControl: false,
          fullscreenControl: false,
          rotateControl: false,
          scaleControl: false,
          // Evita repetição horizontal do mundo e limita o pan ao globo.
          restriction: {
            latLngBounds: { north: 85, south: -85, west: -179.9, east: 179.9 },
            strictBounds: false,
          },
          // Estilos customizados para ocultar POIs (lojas, restaurantes, etc)
          // mas manter ruas e nomes de cidades
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.business',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.park',
              elementType: 'labels.icon',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.school',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.medical',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.government',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.place_of_worship',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'poi.sports_complex',
              stylers: [{ visibility: 'off' }]
            },
            {
              featureType: 'transit.station',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

        // Criar InfoWindow
        this.infoWindow = new google.maps.InfoWindow();

        // Overlay "dummy" só para expor a projeção em pixels (usado no cluster).
        const ProjectionHelper = class extends google.maps.OverlayView {
          onAdd() {}
          draw() {}
          onRemove() {}
        };
        this.projectionOverlay = new ProjectionHelper();
        this.projectionOverlay.setMap(this.map);

        // Criar polígono (equivalente ao componente do Leaflet)
        this.polygon = new google.maps.Polygon({
          map: this.map,
          paths: [],
          strokeColor: '#FF0000',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#FF0000',
          fillOpacity: 0.35,
          editable: false
        });

        // Geolocalização
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition((position) => {
            latLngBegin = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            this.map.setCenter(latLngBegin);
          });
        }

        // Evento de click
        this.map.addListener('click', (e) => {
          this.$emit('click', {
            latlng: {
              lat: e.latLng.lat(),
              lng: e.latLng.lng()
            }
          });
        });

        // Evento de movimentação
        this.map.addListener('idle', () => {
          if (this.event.move) {
            this.event.move(this, this.getInfo());
          }
        });

        // Ocultar todos os controles do Google Maps
        this.css = `<style>
          .gm-style .gmnoprint { display: none !important; }
          .gm-style-mtc { display: none !important; }
          .gm-control-active { display: none !important; }
          .gm-svpc { display: none !important; }
          .gm-style .gm-style-cc { display: none !important; }
        </style>`;

        this.isLoaded = true;
      } catch (error) {
        console.error('Erro ao carregar Google Maps:', error);
      }
    },

    moveArea() {
      this.getInfo();
    },

    addPoints(points, selected = null, cluster = true) {
      this.points = points;

      // REMOVER MARCADORES - suporta tanto marcadores padrão quanto customizados
      this.markerGroup.forEach(marker => {
        if (marker.setMap) {
          // Marcadores padrão do Google Maps
          if (typeof marker.setVisible === 'function') {
            marker.setVisible(false);  // Primeiro esconder
          }
          marker.setMap(null);        // Depois remover
          marker.map = null;          // Força remoção da propriedade interna
        } else if (marker.onRemove) {
          // Marcadores customizados (Leaflet style)
          marker.onRemove();
        }
      });
      this.markerGroup = [];

      // Agrupar pontos sobrepostos na tela antes de renderizar — só quando
      // habilitado (modo dispositivos). No modo previsão/clima, sem cluster.
      const items = cluster ? this.clusterPoints(points, 52, selected) : points;

      const prevSig = this.prevSignatures;
      const newSig = new Set();

      // Adicionar novos marcadores (pontos individuais ou clusters).
      // Anima (scale de entrada) APENAS o que mudou em relação ao render anterior
      // — pins/clusters que continuam iguais não disparam animação (evita a tela
      // inteira "piscar" a cada zoom).
      for (const item of items) {
        const sig = this.itemSignature(item);
        newSig.add(sig);
        const before = this.markerGroup.length;
        if (this.event.add) {
          this.event.add(item, this.markerGroup, this);
        }
        const marker = this.markerGroup[this.markerGroup.length - 1];
        if (marker && this.markerGroup.length > before) {
          // anima só se for assinatura nova (ou primeiro render quando prevSig é null)
          marker._enter = !prevSig || !prevSig.has(sig);
        }
      }

      this.prevSignatures = newSig;
    },

    // Assinatura estável do item para detectar se mudou entre renders.
    itemSignature(item) {
      if (item.isCluster) {
        return "c:" + item.points.map((d) => d.id).sort().join(",");
      }
      const key = item.id != null ? item.id : item.latitude + "," + item.longitude;
      return "p:" + key;
    },

    move(lat, lng, zoom = 12) {
      if (this.map) {
        this.map.setCenter({ lat, lng });
        this.map.setZoom(zoom);
      }
    },

    getZoom() {
      return this.map ? this.map.getZoom() : 12;
    },

    getInfo() {
      if (!this.map) return null;

      const center = this.map.getCenter();
      const bounds = this.map.getBounds();

      if (!bounds) return null;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      return {
        latitude: center.lat(),
        longitude: center.lng(),
        zoom: this.map.getZoom(),
        latitude_max: ne.lat(),
        longitude_max: ne.lng(),
        latitude_min: sw.lat(),
        longitude_min: sw.lng(),
      };
    },

    clickPonto(data) {
      this.$emit("clickponto", data);
    },

    mapUpdate() {
      setTimeout(() => {
        if (this.map) {
          google.maps.event.trigger(this.map, 'resize');
        }
      }, 100);
    },

    // Método para adicionar marcador simples
    addMarker(lat, lng, options = {}) {
      if (!this.map) return null;

      const marker = new google.maps.Marker({
        position: { lat, lng },
        map: this.map,
        ...options
      });

      this.markerGroup.push(marker);
      return marker;
    },

    // Método para adicionar marcador com ícone customizado (SVG)
    addMarkerWithCustomIcon(lat, lng, iconHtml, title = '') {
      if (!this.map) return null;

      const svgDataUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(iconHtml);

      const icon = {
        url: svgDataUrl,
        scaledSize: new google.maps.Size(32, 42),
        anchor: new google.maps.Point(16, 42) // Anchor na ponta inferior do pin
      };

      // Criar marcador SEM definir o mapa primeiro
      const marker = new google.maps.Marker({
        position: { lat, lng },
        icon: icon,
        title: title
      });

      if (title) {
        marker.addListener('click', () => {
          this.infoWindow.setContent(title);
          this.infoWindow.open(this.map, marker);
        });
      }

      // Agora adicionar ao mapa
      marker.setMap(this.map);

      this.markerGroup.push(marker);
      return marker;
    },

    // Método para adicionar marcador com ícone HTML customizado (para compatibilidade com Leaflet divIcon)
    addMarkerWithDivIcon(lat, lng, iconHtml) {
      if (!this.map) return null;

      // Google Maps não suporta divIcon diretamente, então criamos um overlay customizado
      class CustomMarker extends google.maps.OverlayView {
        constructor(position, html, map) {
          super();
          this.position = position;
          this.html = html;
          this.div = null;
          this.clickListeners = [];
          this._enter = false; // anima entrada só se marcado pelo addPoints
          this.setMap(map);
        }

        onAdd() {
          this.div = document.createElement('div');
          this.div.style.position = 'absolute';
          this.div.innerHTML = this.html;
          this.div.style.cursor = 'pointer';

          // Animação sutil de entrada (scale+opacity, origem na ponta) APENAS para
          // markers novos/alterados (this._enter). Pins que continuam iguais entre
          // zooms não animam — evita a tela inteira "piscar".
          // Remoção da classe via setTimeout (mais confiável que animationend em
          // qualquer navegador/WebView; a duração casa com o @keyframes).
          if (this._enter) {
            this.div.classList.add('pin-enter-anim');
            setTimeout(() => {
              if (this.div) this.div.classList.remove('pin-enter-anim');
            }, 360);
          }

          // overlayMouseTarget é o painel interativo do Google Maps — garante
          // que cliques no pin sejam capturados (overlayImage não é interativo).
          const panes = this.getPanes();
          (panes.overlayMouseTarget || panes.overlayImage).appendChild(this.div);

          // Executar listeners que foram adicionados antes do div estar pronto
          this.clickListeners.forEach(listener => {
            this.div.addEventListener('click', listener);
          });

          // Long-press (mobile): segurar pressionado expande o pin (.pin-pressed).
          // No desktop a expansão é via :hover (CSS).
          const pin = this.div.querySelector('.square-pin-icon.has-expand');
          if (pin) {
            let pressTimer = null;
            const startPress = () => {
              pressTimer = setTimeout(() => { pin.classList.add('pin-pressed'); }, 350);
            };
            const cancelPress = () => {
              if (pressTimer) { clearTimeout(pressTimer); pressTimer = null; }
            };
            const releasePress = () => {
              cancelPress();
              // Remove a expansão um instante depois para não conflitar com o clique.
              setTimeout(() => { pin.classList.remove('pin-pressed'); }, 1500);
            };
            this.div.addEventListener('touchstart', startPress, { passive: true });
            this.div.addEventListener('touchend', releasePress);
            this.div.addEventListener('touchcancel', releasePress);
            this.div.addEventListener('touchmove', cancelPress, { passive: true });
          }

          // Evita que o scroll dentro da lista do cluster controle o zoom do mapa.
          const scrollList = this.div.querySelector('.pin-cluster-list');
          if (scrollList) {
            const stop = (e) => { e.stopPropagation(); };
            scrollList.addEventListener('wheel', stop);
            scrollList.addEventListener('mousewheel', stop);
            scrollList.addEventListener('touchmove', (e) => { e.stopPropagation(); }, { passive: true });
          }
        }

        draw() {
          const overlayProjection = this.getProjection();
          const position = overlayProjection.fromLatLngToDivPixel(
            new google.maps.LatLng(this.position.lat, this.position.lng)
          );

          if (this.div) {
            this.div.style.left = position.x + 'px';
            this.div.style.top = position.y + 'px';
          }
        }

        onRemove() {
          if (this.div) {
            this.div.parentNode.removeChild(this.div);
            this.div = null;
          }
        }

        // Método helper para adicionar listeners antes do div estar pronto
        addClickListener(listener) {
          this.clickListeners.push(listener);
          if (this.div) {
            this.div.addEventListener('click', listener);
          }
        }
      }

      const customMarker = new CustomMarker(
        { lat, lng },
        iconHtml,
        this.map
      );

      this.markerGroup.push(customMarker);
      return customMarker;
    },

    // ---------------------------------------------------------------------------------
    // Polígonos de camada (avisos do INMET)
    //
    // O `polygon` acima é outro assunto: é o polígono único e editável da área agrícola.
    // Aqui são N polígonos só de leitura, redesenhados a cada atualização da camada.
    // ---------------------------------------------------------------------------------

    /// Desenha uma camada de polígonos GeoJSON, substituindo a anterior.
    ///
    /// `features` é uma lista de { id, geometria, cor, gravidade, dados }, onde `geometria` é
    /// um GeoJSON Polygon ou MultiPolygon. `onClick` recebe o `dados` do polígono clicado.
    addPolygons(features, onClick) {
      if (!this.map || !window.google) return;

      // CANVAS PRÓPRIO, não google.maps.Polygon.
      //
      // Com Polygon, as áreas de dias anteriores continuavam pintadas depois de trocar o
      // dia: setMap(null), setVisible(false), setPaths([]), panBy, resize e troca de
      // mapTypeId — nada limpava o desenho de forma confiável, mesmo com a nossa lista de
      // objetos comprovadamente correta (1 polígono vivo, 4 manchas na tela). O rastro vive
      // no motor de render do Maps, fora do nosso alcance.
      //
      // Num overlay canvas nosso, cada redesenho começa com clearRect(): não existe estado
      // anterior para vazar. Também é mais barato — uma superfície só em vez de N objetos.
      this.clearPolygons();

      const ordenadas = (features || [])
        .slice()
        // Menos grave primeiro: quem é desenhado depois fica por cima, então o aviso mais
        // grave é o que o usuário vê quando duas áreas se sobrepõem.
        .sort((a, b) => (a.gravidade || 0) - (b.gravidade || 0))
        // Área pré-calculada: o hit-test roda a cada mousemove e desempata por ela.
        .map((f) => ({ ...f, area: this.areaAproximada(f.geometria) }));

      const self = this;

      class CamadaAvisos extends google.maps.OverlayView {
        constructor(itens, aoClicar) {
          super();
          this.itens = itens;
          this.aoClicar = aoClicar;
          this.canvas = null;
          // Id do aviso aberto no painel, para desenhá-lo em destaque.
          this.selecionado = null;
          // Ids dos avisos sob o último clique; clicar de novo sobre a mesma pilha alterna.
          this.ultimaPilha = null;
          this.ouvintes = [];
        }

        onAdd() {
          this.canvas = document.createElement('canvas');
          this.canvas.style.position = 'absolute';
          // O canvas não recebe eventos: se recebesse, engoliria o arrasto e o zoom do mapa
          // em toda a área coberta por um aviso — que chega a ser o país inteiro. Os cliques
          // são capturados no mapa e testados contra as geometrias (ver aoClicarNoMapa).
          this.canvas.style.pointerEvents = 'none';
          this.getPanes().overlayLayer.appendChild(this.canvas);

          const mapa = this.getMap();
          this.ouvintes.push(
            mapa.addListener('click', (e) => this.aoClicarNoMapa(e)),
            // Cursor de mão sobre uma área clicável, como em qualquer elemento interativo.
            mapa.addListener('mousemove', (e) => {
              const alvo = this.avisoNoPonto(e.latLng);
              mapa.setOptions({ draggableCursor: alvo ? 'pointer' : null });
            })
          );
        }

        /// Abre o aviso sob o clique. Sem aviso ali, o clique segue sendo do mapa.
        ///
        /// Clicar de novo sobre o MESMO conjunto de áreas sobrepostas alterna para o próximo
        /// aviso: sem isso, um aviso cuja área coincide com (ou é coberta por) outra só
        /// abriria pelo ícone.
        aoClicarNoMapa(evento) {
          if (typeof this.aoClicar !== 'function') return;

          const candidatos = this.avisosNoPonto(evento.latLng);
          if (!candidatos.length) return;

          const chave = candidatos.map((c) => c.id).join('|');
          const atual = candidatos.findIndex((c) => c.id === this.selecionado);
          const alvo =
            chave === this.ultimaPilha && atual !== -1
              ? candidatos[(atual + 1) % candidatos.length]
              : candidatos[0];
          this.ultimaPilha = chave;

          this.selecionado = alvo.id;
          this.draw();
          this.aoClicar(alvo.dados || alvo);
        }

        /// Avisos sob uma coordenada, na ordem de preferência para o clique.
        ///
        /// O MENOR em área vem primeiro: quem clica dentro de uma área contida noutra maior
        /// quer a de dentro — a maior segue clicável em todo o resto dela. Antes a regra era
        /// "o mais grave", e um aviso grave cobrindo um estado inteiro tornava inclicáveis
        /// os avisos menores dentro dele. Empate de área fica com o mais grave.
        avisosNoPonto(latLng) {
          if (!latLng) return [];

          return this.itens
            .filter((item) => self.pontoDentroDaGeometria(latLng, item.geometria))
            .sort(
              (a, b) =>
                a.area - b.area || (b.gravidade || 0) - (a.gravidade || 0)
            );
        }

        avisoNoPonto(latLng) {
          return this.avisosNoPonto(latLng)[0] || null;
        }

        draw() {
          if (!this.canvas) return;

          const projecao = this.getProjection();
          if (!projecao) return;

          const mapa = this.getMap();
          const div = mapa.getDiv();
          const largura = div.offsetWidth;
          const altura = div.offsetHeight;

          // O overlayLayer é posicionado em coordenadas do mundo, não da tela: ancoramos o
          // canvas no canto noroeste do viewport para os pixels baterem com o que se vê.
          const limites = mapa.getBounds();
          if (!limites) return;

          const noroeste = new google.maps.LatLng(
            limites.getNorthEast().lat(),
            limites.getSouthWest().lng()
          );
          const origem = projecao.fromLatLngToDivPixel(noroeste);

          // devicePixelRatio evita o traço serrilhado em tela de alta densidade.
          const escala = window.devicePixelRatio || 1;
          this.canvas.width = largura * escala;
          this.canvas.height = altura * escala;
          this.canvas.style.width = largura + 'px';
          this.canvas.style.height = altura + 'px';
          this.canvas.style.left = origem.x + 'px';
          this.canvas.style.top = origem.y + 'px';

          const ctx = this.canvas.getContext('2d');
          // A linha que resolve o bug: cada frame parte de uma superfície limpa.
          ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
          ctx.scale(escala, escala);

          // O selecionado é desenhado POR ÚLTIMO, para o contorno de destaque não ficar
          // por baixo de uma área sobreposta — é justamente quando há sobreposição que o
          // destaque precisa aparecer.
          const naOrdem = this.itens
            .filter((i) => i.id !== this.selecionado)
            .concat(this.itens.filter((i) => i.id === this.selecionado));

          naOrdem.forEach((item) => {
            const aneis = self.geoJsonParaPaths(item.geometria);
            if (!aneis.length) return;

            const cor = item.cor || '#9e9e9e';
            const selecionado = item.id === this.selecionado;

            ctx.beginPath();
            aneis.forEach((anel) => {
              anel.forEach((ponto, i) => {
                const pos = projecao.fromLatLngToDivPixel(
                  new google.maps.LatLng(ponto.lat, ponto.lng)
                );
                const x = pos.x - origem.x;
                const y = pos.y - origem.y;
                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
              });
              ctx.closePath();
            });

            // Aviso ainda não vigente fica só com o contorno, mais grosso: o polígono de um
            // alerta de depois de amanhã é idêntico ao de um vigente, e preenchê-los igual
            // faria uma previsão parecer um evento em curso.
            if (!item.futuro) {
              ctx.fillStyle = cor;
              // Baixa de propósito: são áreas enormes (chegam a cobrir meio país) e,
              // sobrepostas, qualquer opacidade maior vira uma mancha só, escondendo
              // cidades, estradas e os pins das outras camadas. O selecionado sobe um
              // pouco, o suficiente para se destacar sem apagar o que está por baixo.
              ctx.globalAlpha = selecionado ? 0.32 : 0.22;
              ctx.fill('evenodd'); // evenodd recorta os anéis internos (buracos)
              ctx.globalAlpha = 1;
            }

            // Selecionado: contorno escurecido e mais grosso. Escurecer a PRÓPRIA cor, em
            // vez de usar um destaque fixo, preserva a identidade do aviso — amarelo,
            // laranja e vermelho continuam distinguíveis entre si quando selecionados.
            ctx.strokeStyle = selecionado ? self.escurecerCor(cor, 0.45) : cor;
            ctx.lineWidth = selecionado ? 3.5 : item.futuro ? 2.5 : 1.5;
            ctx.globalAlpha = selecionado ? 1 : 0.9;
            ctx.stroke();
            ctx.globalAlpha = 1;
          });
        }

        onRemove() {
          this.ouvintes.forEach((o) => google.maps.event.removeListener(o));
          this.ouvintes = [];

          const mapa = this.getMap();
          if (mapa) mapa.setOptions({ draggableCursor: null });

          if (this.canvas && this.canvas.parentNode) {
            this.canvas.parentNode.removeChild(this.canvas);
          }
          this.canvas = null;
        }
      }

      this.avisosOverlay = new CamadaAvisos(ordenadas, onClick);
      this.avisosOverlay.setMap(this.map);

      // Os ícones também abrem o aviso: são o alvo óbvio para quem quer um aviso específico
      // numa região com várias áreas empilhadas.
      ordenadas.forEach((feature) => {
        if (!feature.icone) return;
        const caminhos = this.geoJsonParaPaths(feature.geometria);
        if (!caminhos.length) return;

        this.adicionarIconeAviso(feature, caminhos, (dados) => {
          if (this.avisosOverlay) {
            this.avisosOverlay.selecionado = feature.id;
            this.avisosOverlay.draw();
          }
          if (typeof onClick === 'function') onClick(dados);
        });
      });
    },

    /// Marca (ou desmarca, com null) o aviso destacado no mapa.
    ///
    /// Existe para o Dashboard sincronizar o mapa quando o painel é fechado por fora — pelo
    /// X do aside, por exemplo —, senão o contorno de destaque ficaria aceso sem painel.
    setPolygonSelecionado(id) {
      if (!this.avisosOverlay) return;
      this.avisosOverlay.selecionado = id || null;
      this.avisosOverlay.draw();
    },

    /// Escurece uma cor #RRGGBB por um fator (0 = igual, 1 = preto).
    escurecerCor(cor, fator) {
      const m = /^#?([0-9a-f]{6})$/i.exec(String(cor).trim());
      if (!m) return cor;

      const n = parseInt(m[1], 16);
      const escurece = (c) => Math.max(0, Math.round(c * (1 - fator)));
      const r = escurece((n >> 16) & 255);
      const g = escurece((n >> 8) & 255);
      const b = escurece(n & 255);

      return 'rgb(' + r + ',' + g + ',' + b + ')';
    },

    /// Área de uma geometria GeoJSON em graus² (shoelace), só para COMPARAR áreas entre si.
    ///
    /// Não é área real — a distorção de lat/lng é irrelevante para decidir qual de dois
    /// avisos sobrepostos, na mesma região, é o menor. Conta só o anel externo de cada
    /// polígono: os buracos mudariam pouco e complicariam à toa.
    areaAproximada(geometria) {
      if (!geometria || !geometria.coordinates) return Infinity;

      const poligonos =
        geometria.type === 'MultiPolygon'
          ? geometria.coordinates
          : geometria.type === 'Polygon'
          ? [geometria.coordinates]
          : [];

      let total = 0;
      poligonos.forEach((poligono) => {
        const anel = (poligono && poligono[0]) || [];
        let soma = 0;
        for (let i = 0, j = anel.length - 1; i < anel.length; j = i++) {
          soma += (Number(anel[j][0]) + Number(anel[i][0])) * (Number(anel[j][1]) - Number(anel[i][1]));
        }
        total += Math.abs(soma) / 2;
      });

      return Number.isFinite(total) && total > 0 ? total : Infinity;
    },

    /// Ponto dentro de uma geometria GeoJSON (Polygon/MultiPolygon).
    ///
    /// Ray casting em lat/lng, não em pixels: assim o teste independe do zoom e não precisa
    /// da projeção. Anéis além do primeiro são buracos — estar dentro de um deles é estar
    /// FORA do polígono, e a contagem par/ímpar já resolve isso naturalmente.
    pontoDentroDaGeometria(latLng, geometria) {
      const aneis = this.geoJsonParaPaths(geometria);
      if (!aneis.length) return false;

      const x = latLng.lng();
      const y = latLng.lat();
      let dentro = false;

      aneis.forEach((anel) => {
        for (let i = 0, j = anel.length - 1; i < anel.length; j = i++) {
          const xi = anel[i].lng, yi = anel[i].lat;
          const xj = anel[j].lng, yj = anel[j].lat;

          const cruza =
            yi > y !== yj > y &&
            x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

          if (cruza) dentro = !dentro;
        }
      });

      return dentro;
    },

    /// Coloca o pictograma do aviso no centro visual da área.
    ///
    /// Usa o overlay HTML de addMarkerWithDivIcon, NÃO um google.maps.Marker: o Marker
    /// deixa o nó da imagem órfão no DOM depois de setMap(null), e o ícone do dia anterior
    /// reaparecia no próximo redesenho do mapa (num zoom, tipicamente). O OverlayView tem
    /// onRemove(), que apaga o nó explicitamente — comportamento determinístico, e o mesmo
    /// caminho que os pins das outras camadas já usam.
    adicionarIconeAviso(feature, caminhos, onClick) {
      const centro = this.centroDosPaths(caminhos);
      if (!centro) return;

      // Os pictogramas do INMET são placas com haste, ~36x53 (proporção ~0,68) — NÃO são
      // quadrados. Forçar um quadrado achata o desenho.
      const altura = 44;
      const largura = Math.round(altura * 0.68);

      // translate(-50%, -100%) ancora no PÉ da haste: a placa aponta para o ponto, como no
      // mapa do INMET, em vez de ficar metade acima e metade abaixo dele.
      const html =
        '<div style="position:relative;transform:translate(-50%,-100%);' +
        'width:' + largura + 'px;height:' + altura + 'px;cursor:pointer;" title="' +
        this.escaparAtributoMapa(feature.titulo || '') + '">' +
        '<img src="' + feature.icone + '" alt="" ' +
        'style="width:100%;height:100%;object-fit:contain;display:block;" />' +
        '</div>';

      const marcador = this.addMarkerWithDivIcon(centro.lat, centro.lng, html);
      if (!marcador) return;

      // addMarkerWithDivIcon empurra para markerGroup (limpo por addPoints); tirar de lá e
      // pôr em layerPolygons deixa o ciclo de vida com a camada: sem isso, um redesenho de
      // pontos apagaria os ícones, e clearPolygons não os alcançaria.
      const i = this.markerGroup.indexOf(marcador);
      if (i !== -1) this.markerGroup.splice(i, 1);

      if (typeof onClick === 'function') {
        marcador.addClickListener(() => onClick(feature.dados || feature));
      }

      this.layerIcons.push(marcador);
    },

    /// Remove só os ícones dos avisos, preservando o pool de polígonos.
    clearLayerIcons() {
      this.layerIcons.forEach((icone) => {
        google.maps.event.clearInstanceListeners(icone);
        icone.setMap(null);
        // onRemove() apaga o nó na hora; sem isso o ícone do dia anterior ficava na tela
        // até o mapa se redesenhar por conta própria (num zoom, tipicamente).
        if (typeof icone.onRemove === 'function') icone.onRemove();
      });
      this.layerIcons = [];
    },

    /// Escapa texto que vai para um atributo HTML montado como string.
    ///
    /// O título vem de dados do INMET (origem de terceiros) e é concatenado em markup —
    /// sem escapar, uma aspa no texto quebra o atributo e abre espaço para injeção.
    escaparAtributoMapa(texto) {
      return String(texto == null ? '' : texto)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },

    /// Centro aproximado de um conjunto de paths, para ancorar o ícone.
    ///
    /// Usa o centro da caixa delimitadora do MAIOR anel, não a média de todos os pontos: a
    /// média é puxada para onde há mais vértices, e as áreas do INMET têm o contorno da
    /// costa muito mais detalhado que o interior — o ícone acabaria no mar.
    centroDosPaths(caminhos) {
      let maior = null;
      caminhos.forEach((anel) => {
        if (!maior || anel.length > maior.length) maior = anel;
      });

      if (!maior || !maior.length) return null;

      let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
      maior.forEach((ponto) => {
        if (ponto.lat < minLat) minLat = ponto.lat;
        if (ponto.lat > maxLat) maxLat = ponto.lat;
        if (ponto.lng < minLng) minLng = ponto.lng;
        if (ponto.lng > maxLng) maxLng = ponto.lng;
      });

      return { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };
    },

    /// Remove tudo que a camada desenhou — polígonos e os ícones que os rotulam.
    ///
    /// Precisa existir separado porque o `markerGroup` de addPoints só limpa os markers dos
    /// pontos: sem isto, desligar a camada deixaria os polígonos desenhados para sempre.
    clearPolygons() {
      this.clearLayerIcons();

      if (this.avisosOverlay) {
        this.avisosOverlay.setMap(null);
        // onRemove() tira o canvas do DOM na hora; sem isso ele ficaria pendurado até o
        // Maps decidir reciclar o overlay.
        if (typeof this.avisosOverlay.onRemove === 'function') this.avisosOverlay.onRemove();
        this.avisosOverlay = null;
      }

      // Sobras de versões anteriores desta camada (quando os avisos eram Polygon).
      this.layerPolygons.forEach((poligono) => {
        google.maps.event.clearInstanceListeners(poligono);
        poligono.setMap(null);
      });
      this.layerPolygons = [];
    },

    /// Alias mantido para o unmount — clearPolygons já faz a limpeza completa.
    destroyPolygons() {
      this.clearPolygons();
    },

    /// Converte a geometria GeoJSON no formato de paths do Google Maps.
    ///
    /// Dois detalhes que quebram silenciosamente se ignorados:
    ///  - GeoJSON é [longitude, latitude]; o Google Maps é {lat, lng}. Invertido, o polígono
    ///    aparece no lugar errado do mundo em vez de dar erro.
    ///  - Um anel além do primeiro é um BURACO no polígono. Passar todos os anéis como paths
    ///    faz o Google Maps recortá-los corretamente, então basta achatá-los.
    geoJsonParaPaths(geometria) {
      if (!geometria || !geometria.coordinates) return [];

      const aneis =
        geometria.type === 'MultiPolygon'
          ? geometria.coordinates.flat()
          : geometria.type === 'Polygon'
          ? geometria.coordinates
          : [];

      return aneis
        .map((anel) =>
          (anel || [])
            .filter((par) => Array.isArray(par) && par.length >= 2)
            .map((par) => ({ lat: Number(par[1]), lng: Number(par[0]) }))
            .filter((ponto) => Number.isFinite(ponto.lat) && Number.isFinite(ponto.lng))
        )
        .filter((anel) => anel.length >= 3); // menos que isso não fecha uma área
    },

    // Método para atualizar o polígono (equivalente ao componente.setLatLngs do Leaflet)
    setPolygonPath(path) {
      if (this.polygon) {
        const googlePath = path.map(point => ({
          lat: point.latitude || point.lat || point[0],
          lng: point.longitude || point.lng || point[1]
        }));
        this.polygon.setPath(googlePath);
      }
    },

    // Obter o caminho do polígono
    getPolygonPath() {
      if (this.polygon) {
        const path = this.polygon.getPath();
        const result = [];
        for (let i = 0; i < path.getLength(); i++) {
          const point = path.getAt(i);
          result.push([point.lat(), point.lng()]);
        }
        return result;
      }
      return [];
    },

    // Método para filtrar pontos baseado no viewport do mapa
    getPontosFiltrado(data, info, max = null, selected) {
      // Sem bounds (mapa ainda inicializando) não há como filtrar por área. Devolver vazio
      // em vez de estourar: quem chama redesenha no 'idle', quando info já existe.
      if (!data || !info) return [];

      var res = [];
      for (const ponto of data) {
        if (selected && selected.id == ponto.id) {
          res.push(ponto);
        } else if (info.latitude_min <= ponto.latitude && info.latitude_max >= ponto.latitude && info.longitude_min <= ponto.longitude && info.longitude_max >= ponto.longitude) {
          if (!(max != null && max < res.length)) {
            res.push(ponto);
          }
        }
      }
      return res;
    },

    // Converte lat/lng para pixels de tela usando a projeção atual do mapa.
    latLngToPixel(lat, lng) {
      if (!this.projectionOverlay) return null;
      const proj = this.projectionOverlay.getProjection();
      if (!proj) return null;
      const p = proj.fromLatLngToContainerPixel(
        new google.maps.LatLng(parseFloat(lat), parseFloat(lng))
      );
      return p ? { x: p.x, y: p.y } : null;
    },

    // Agrupa pontos que se sobrepõem na tela por DISTÂNCIA em pixels (raio).
    // Não usa grade fixa (que tem "fronteiras"): qualquer ponto a menos de
    // pxRadius de outro entra no mesmo grupo (expansão tipo flood). Funciona em
    // qualquer zoom/tela pois usa a projeção em pixels do próprio mapa. O ponto
    // selecionado nunca é agrupado.
    clusterPoints(points, pxRadius = 52, selected = null) {
      const proj = this.projectionOverlay && this.projectionOverlay.getProjection();
      if (!proj) return points; // sem projeção ainda: sem cluster

      const singles = [];
      const located = []; // { point, x, y, used }

      for (const point of points) {
        if (selected && selected.id == point.id) {
          singles.push(point);
          continue;
        }
        const px = this.latLngToPixel(point.latitude, point.longitude);
        if (!px) {
          singles.push(point);
          continue;
        }
        located.push({ point, x: px.x, y: px.y, used: false });
      }

      const r2 = pxRadius * pxRadius;
      const result = [...singles];

      for (let i = 0; i < located.length; i++) {
        if (located[i].used) continue;
        located[i].used = true;
        const group = [located[i]];

        // Flood: agrega vizinhos a menos de pxRadius de qualquer membro do grupo.
        for (let g = 0; g < group.length; g++) {
          for (let j = 0; j < located.length; j++) {
            if (located[j].used) continue;
            const dx = group[g].x - located[j].x;
            const dy = group[g].y - located[j].y;
            if (dx * dx + dy * dy <= r2) {
              located[j].used = true;
              group.push(located[j]);
            }
          }
        }

        if (group.length === 1) {
          result.push(group[0].point);
        } else {
          let latSum = 0, lngSum = 0;
          for (const m of group) {
            latSum += parseFloat(m.point.latitude);
            lngSum += parseFloat(m.point.longitude);
          }
          result.push({
            isCluster: true,
            id: "cluster-" + group.map(m => m.point.id).join("-"),
            latitude: latSum / group.length,
            longitude: lngSum / group.length,
            points: group.map(m => m.point),
          });
        }
      }
      return result;
    },
  },

  mounted() {
    this.initMap();
  },

  beforeUnmount() {
    // Limpar marcadores
    this.markerGroup.forEach(marker => {
      if (marker.setMap) {
        marker.setMap(null);
      }
    });
    this.markerGroup = [];

    // Limpar polígono
    if (this.polygon) {
      this.polygon.setMap(null);
      this.polygon = null;
    }

    // Limpar polígonos de camada. Não basta soltar this.map: o listener de clique de cada
    // polígono captura o callback do Dashboard, que retém o componente inteiro (com os
    // avisos e suas geometrias) enquanto o objeto de mapa do Google viver.
    this.destroyPolygons();

    // Limpar mapa
    if (this.map) {
      this.map = null;
    }
  },

  watch: {
    mapTypeId(newValue) {
      if (this.map && newValue) {
        this.map.setMapTypeId(newValue);
      }
    }
  }
};
</script>

<style>
/* Animação de entrada do marker (suaviza o surgir ao mudar zoom). Aplica no
   div do overlay; origem na base (ponta). Roda uma vez e é removida. */
@keyframes pinEnter {
  0%   { opacity: 0; transform: scale(0.4); }
  60%  { opacity: 1; transform: scale(1.06); }
  100% { opacity: 1; transform: scale(1); }
}
.pin-enter-anim {
  animation: pinEnter 0.3s cubic-bezier(0.34, 1.2, 0.5, 1);
  transform-origin: 22px bottom; /* ~ponta do pin (centro horizontal do wrapper) */
}

/* Troca ícone↔dado dentro do pin: o novo conteúdo "assenta" (scale+opacity)
   para suavizar a alternância. Roda a cada swap (elemento recriado). */
@keyframes pinContentSwap {
  0%   { opacity: 0; transform: scale(0.55); }
  60%  { opacity: 1; transform: scale(1.08); }
  100% { opacity: 1; transform: scale(1); }
}
.pin-content-anim {
  animation: pinContentSwap 0.28s cubic-bezier(0.34, 1.2, 0.5, 1);
  transform-origin: center;
}

/* ===== Pin do mapa (novo design) ===== */
.square-pin-icon {
  display: inline-block;
  position: absolute;
  top: -46px;
  left: -22px;
  pointer-events: auto;
  cursor: pointer;
}
.square-pin-icon-container {
  display: flex;
  width: 44px;
  height: 34px;
  border-radius: 11px;
  font-size: 14px;
  background-color: #ffffff;
  box-shadow: 0px 8px 16px -4px rgba(0, 0, 0, 0.35);
  position: relative;
  pointer-events: auto;
}
.square-pin-icon-text-container {
  margin: auto;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 28px;
  position: relative;
  font-size: 13px;
  border-radius: 9px;
  overflow: hidden;
}
.square-pin-icon-text {
  margin: auto;
  color: #ffffff;
  font-weight: 800;
  font-family: "Arial", sans-serif;
  letter-spacing: -0.3px;
  white-space: nowrap;
}
.square-pin-icon-img {
  width: 18px;
  height: 18px;
  object-fit: contain;
  display: block;
}
/* Ícone branco escurecido para o pin público (fundo branco). */
.square-pin-icon-img--dark {
  filter: brightness(0) saturate(100%) invert(20%);
}
.square-pin-icon-point {
  position: absolute;
  top: 34px;            /* começa na base externa do card, sem cobrir a borda */
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  margin: auto;
  border-top: 11px solid #ffffff;
  left: 15px;
  filter: drop-shadow(0px 4px 3px rgba(0, 0, 0, 0.2));
}

/* ===== Card expandido (hover / long-press) ===== */
/* Card branco; só o badge do ícone leva a cor do sensor. Largura ajusta ao
   conteúdo (não estica). */
.square-pin-icon .square-pin-expanded {
  display: none;
  position: absolute;
  /* Base ancorada na linha da ponta (top:34px) e cresce para CIMA, deixando o
     ponteiro visível embaixo (sem cobrir a ponta) e sem encolher o card. */
  top: 34px;
  left: 0;
  width: max-content;
  max-width: 220px;
  height: 48px;
  border-radius: 13px;
  background-color: #ffffff;
  box-shadow: 0px 8px 16px -4px rgba(0, 0, 0, 0.35);
  transform: translateY(-100%);
  pointer-events: auto;
  cursor: pointer;
  z-index: 5;
}
.pin-expanded-body {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  /* gap substituído por margin nos filhos (compat. WebView/Safari antigos) */
  width: calc(100% - 6px);
  height: calc(100% - 6px);
  margin: 3px;
  padding: 0 12px 0 3px;
  border-radius: 9px;
  background-color: #ffffff;
  box-sizing: border-box;
  overflow: hidden;
}
/* Badge colorido só atrás do ícone. */
.pin-expanded-badge {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 9px;
}
.pin-expanded-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
}
.pin-expanded-info {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  overflow: hidden;
  margin-left: 8px; /* substitui o gap do flex (compat. WebView antigos) */
}
.pin-expanded-name {
  color: #333333;
  font-size: 11px;
  font-weight: 600;
  font-family: "Arial", sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pin-expanded-value {
  color: #111111;
  font-size: 15px;
  font-weight: 800;
  font-family: "Arial", sans-serif;
}
/* Mostra o card expandido e esconde o compacto no hover (desktop) ou
   quando .pin-pressed é aplicado via long-press (mobile). */
.square-pin-icon.has-expand:hover .square-pin-icon-container,
.square-pin-icon.has-expand.pin-pressed .square-pin-icon-container {
  display: none;
}
.square-pin-icon.has-expand:hover .square-pin-expanded,
.square-pin-icon.has-expand.pin-pressed .square-pin-expanded {
  display: flex;
}

/* Animação de expansão do cluster: em vez de display none/flex (não animável),
   usa opacity + scale com origem no ponteiro (base/centro) para parecer que
   "cresce" a partir do pin. Mantém o transform de posicionamento. */
.square-pin-icon.is-cluster .square-pin-expanded.square-pin-cluster-card {
  display: flex;                 /* sempre no layout; visibilidade via opacity */
  opacity: 0;
  visibility: hidden;
  transform-origin: bottom center;
  transform: translate(-50%, -100%) scale(0.6);
  transition: opacity 0.16s ease, transform 0.18s cubic-bezier(0.2, 0.8, 0.3, 1.1), visibility 0s linear 0.18s;
  pointer-events: none;
}
.square-pin-icon.is-cluster.has-expand:hover .square-pin-expanded.square-pin-cluster-card,
.square-pin-icon.is-cluster.has-expand.pin-pressed .square-pin-expanded.square-pin-cluster-card {
  opacity: 1;
  visibility: visible;
  transform: translate(-50%, -100%) scale(1);
  transition: opacity 0.16s ease, transform 0.18s cubic-bezier(0.2, 0.8, 0.3, 1.1);
  pointer-events: auto;
}

/* Mesma animação suave no card do pin individual (cresce a partir da ponta).
   O transform de posição é translateY(-100%) (sem X). */
.square-pin-icon .square-pin-expanded:not(.square-pin-cluster-card) {
  display: flex;
  opacity: 0;
  visibility: hidden;
  transform-origin: bottom left;
  transform: translateY(-100%) scale(0.6);
  transition: opacity 0.16s ease, transform 0.18s cubic-bezier(0.2, 0.8, 0.3, 1.1), visibility 0s linear 0.18s;
  pointer-events: none;
}
.square-pin-icon.has-expand:hover .square-pin-expanded:not(.square-pin-cluster-card),
.square-pin-icon.has-expand.pin-pressed .square-pin-expanded:not(.square-pin-cluster-card) {
  opacity: 1;
  visibility: visible;
  transform: translateY(-100%) scale(1);
  transition: opacity 0.16s ease, transform 0.18s cubic-bezier(0.2, 0.8, 0.3, 1.1);
  pointer-events: auto;
}
/* No card expandido público o texto/ícone ficam escuros. */
.square-pin-icon.public-station .pin-expanded-name,
.square-pin-icon.public-station .pin-expanded-value {
  color: #333333;
}
.square-pin-icon.public-station .pin-expanded-icon {
  filter: brightness(0) saturate(100%) invert(20%);
}
/* Pin público: cantos mais arredondados que os pins coloridos. */
.square-pin-icon.public-station .square-pin-icon-container {
  border-radius: 17px;
  border: 2px solid #10C537; /* borda verde do pin público */
  box-sizing: border-box;
}
.square-pin-icon.public-station .square-pin-icon-text-container {
  border-radius: 15px;
}
/* Ponteiro do pin público: branco normalmente, verde quando selecionado. */
.square-pin-icon.public-station .square-pin-icon-point {
  border-top-color: #E8F8E6;
}
.square-pin-icon.public-station.is-selected .square-pin-icon-point {
  border-top-color: #10C537;
}

/* ===== Cluster: borda verde estática (sem animação) ===== */
.pin-cluster-static {
  position: relative;
  z-index: 0;
  overflow: hidden;
}
.pin-cluster-static::before {
  content: "";
  position: absolute;
  z-index: -2;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: #78A64E;
  border-radius: inherit;
}
.pin-cluster-static::after {
  content: "";
  position: absolute;
  z-index: -1;
  left: 3px;
  top: 3px;
  width: calc(100% - 6px);
  height: calc(100% - 6px);
  background: #ffffff;
  border-radius: 9px;
}

/* ===== Cluster: pin compacto (retangular: nº no badge + "dispositivos") =====
   Centralizado no ponto do mapa: o wrapper fica em left:-22px, então o ponto
   real está em x=22 dentro dele. Posiciono pin, ponteiro e card todos centrados
   em x=22 (left:22px + translateX(-50%)) para o pin fechado e o expandido
   compartilharem o mesmo eixo — sem sensação de teletransporte. */
.square-pin-icon-container.pin-cluster-compact {
  position: absolute;
  top: 0;
  left: 22px;
  transform: translateX(-50%);
  width: 96px;
  height: 66px;
  background-color: #ffffff;
  padding: 0;
}
.pin-cluster-compact-body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* gap substituído por margin no .pin-cluster-count */
  margin: 2px;            /* espessura da borda do cluster */
  padding: 3px;
  border-radius: 10px;
  background-color: #ffffff;
  width: calc(100% - 4px);
  height: calc(100% - 4px);
  box-sizing: border-box;
}
.pin-cluster-badge {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 25px;
  padding: 0;
  border-radius: 7px;
  background-color: #FFF800;
  color: #24400B;
  font-size: 18px;
  font-weight: 500;
  font-family: "Arial", sans-serif;
  box-sizing: border-box;
}
.pin-cluster-count {
  color: #24400B;
  font-size: 13px;
  font-weight: 500;
  font-family: "Arial", sans-serif;
  white-space: nowrap;
  margin-top: 5px; /* substitui o gap do flex (compat. WebView antigos) */
}
/* Ponteiro do pin de cluster: no ponto do mapa (x=22), apontando pra baixo.
   top = altura do pin (66px) para sair da base sem cobrir a borda verde. */
.square-pin-icon.is-cluster .square-pin-icon-point {
  top: 66px;
  left: 22px;
  transform: translateX(-50%);
}

/* ===== Cluster: card expandido com lista rolável (abre para CIMA) ===== */
/* A base do card fica fixa na linha de baixo do pin (top:43px) e o card cresce
   para cima via translateY(-100%), independente da altura da lista. O ponteiro
   fica na BASE do card (não no pin), então acompanha o card e aponta para o
   mesmo ponto do mapa — sensação de expansão, não de teletransporte. */
.square-pin-icon .square-pin-expanded.square-pin-cluster-card {
  height: auto;
  width: 220px;
  max-width: 240px;
  top: 66px;        /* base do card alinhada ao topo do ponteiro do pin fechado */
  bottom: auto;
  left: 22px;       /* centro no ponto do mapa (mesmo eixo do pin fechado) */
  transform: translate(-50%, -100%); /* centraliza X + cresce pra cima */
  overflow: visible; /* deixa o ponteiro aparecer abaixo do card */
}
/* Ponteiro do card: encostado na base, centralizado, apontando para baixo —
   mesma posição/altura do ponteiro do pin fechado (top:48px + 10px da seta). */
.pin-cluster-point {
  display: block;
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 10px solid #ffffff;
  filter: drop-shadow(0px 4px 3px rgba(0, 0, 0, 0.2));
  z-index: 2;
}
/* Quando o cluster está expandido, esconde o ponteiro do pin compacto (ele
   ficaria órfão lá embaixo); o ponteiro do card assume. */
.square-pin-icon.is-cluster.has-expand:hover .square-pin-icon-point,
.square-pin-icon.is-cluster.has-expand.pin-pressed .square-pin-icon-point {
  display: none;
}
.pin-cluster-body {
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  height: auto;
  margin: 2px;            /* espessura da borda do card de cluster */
  width: calc(100% - 4px);
  padding: 10px 8px;
  overflow: visible;
}
.pin-cluster-list {
  display: flex;
  flex-direction: column;
  /* gap substituído por margin nas linhas (compat. WebView antigos) */
  max-height: 280px; /* ~7 linhas */
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px;
}
.pin-cluster-row {
  display: flex;
  align-items: center;
}
.pin-cluster-row + .pin-cluster-row {
  margin-top: 10px; /* espaço entre linhas (substitui o gap) */
}
.pin-cluster-row .pin-expanded-info {
  margin-left: 10px; /* espaço badge→texto na linha do cluster */
}
.pin-cluster-row .pin-expanded-badge {
  width: 34px;
  height: 34px;
}
.pin-cluster-row .pin-expanded-icon {
  width: 22px;
  height: 22px;
}
.pin-cluster-list::-webkit-scrollbar {
  width: 5px;
}
.pin-cluster-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}
.map-area {
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 900;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.map-body {
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 1000;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
</style>
